import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { Item, Section } from './types'; // Import shared types

const ViewFormDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [form, setForm] = useState<any>(null);
  const [notes, setNotes] = useState('');
  const [dueDate, setDueDate] = useState('');

  useEffect(() => {
    fetchForm();
  }, [id]);

  const fetchForm = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/Forms/id/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setForm(response.data);
      setNotes(response.data.notes || '');
      setDueDate(response.data.dueDate ? new Date(response.data.dueDate).toISOString().split('T')[0] : '');
    } catch (error) {
      console.error('Failed to fetch form details:', error);
      alert('Failed to load form details. Please try again.');
    }
  };

  const updateNotes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/Forms/${id}/notes`,
        { notes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setForm(response.data);
      setNotes(response.data.notes || '');
      alert('Notes updated!');
    } catch (error) {
      console.error('Failed to update notes:', error);
      alert('Failed to update notes. Please try again.');
    }
  };

  const updateDueDate = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/Forms/${id}/due-date`,
        { dueDate: new Date(dueDate).toISOString() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setForm(response.data);
      setDueDate(response.data.dueDate ? new Date(response.data.dueDate).toISOString().split('T')[0] : '');
      alert('Due date updated!');
    } catch (error) {
      console.error('Failed to update due date:', error);
      alert('Failed to update due date. Please try again.');
    }
  };

  const deleteForm = async () => {
    if (!window.confirm('Are you sure you want to delete this form? This action cannot be undone.')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/Forms/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      alert('Form deleted successfully!');
      navigate('/view-forms');
    } catch (error: any) {
      console.error('Failed to delete form:', error);
      if (error.response && error.response.status === 404) {
        alert('Form not found or you do not have permission to delete it.');
      } else {
        alert('Failed to delete form. Please try again.');
      }
    }
  };

  const sanitizeText = (text: string): string => {
    return text
      .replace(/\$i\$/gi, 'I')
      .replace(/&/g, 'and')
      .replace(/[^a-zA-Z0-9\s.,\/-]/g, '');
  };

  const exportToPDF = async () => {
    const element = document.querySelector('.container.mt-5') as HTMLElement;
    if (!element) {
      alert('Error: Could not find the content to export.');
      return;
    }

    try {
      const originalOverflow = element.style.overflow;
      element.style.overflow = 'visible';

      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: true,
        backgroundColor: '#ffffff',
      });

      const imgData = canvas.toDataURL('image/png');
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });

      const imgWidth = 190;
      const pageHeight = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      doc.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        doc.addPage();
        doc.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      doc.save(`${sanitizeText(form?.name || `form_${form?.id}`)}.pdf`);
      element.style.overflow = originalOverflow;
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    }
  };

  if (!form) return <div className="container mt-5"><div className="alert alert-info">Loading...</div></div>;

  const formData = JSON.parse(form.jsonData || '{}');

  const renderItemContent = (item: Item) => {
    switch (item.type) {
      case 'song':
        return (
          <>
            <strong>{item.label}:</strong> {item.song || 'Not specified'}{' '}
            {item.clientNotes && <span>(Client Notes: {item.clientNotes})</span>}
          </>
        );
      case 'introduction':
        return (
          <>
            <strong>Name:</strong> {item.name || 'Not specified'}{' '}
            <strong>Song:</strong> {item.song || 'Not specified'}{' '}
            {item.clientNotes && <span>(Client Notes: {item.clientNotes})</span>}
          </>
        );
      case 'timeline':
        return (
          <>
            <strong>Event:</strong> {item.event || 'Not specified'}{' '}
            <strong>Time:</strong> {item.time || 'Not specified'}{' '}
            {item.clientNotes && <span>(Client Notes: {item.clientNotes})</span>}
          </>
        );
      case 'general':
        return (
          <>
            <strong>{item.key}:</strong> {item.value || 'Not specified'}{' '}
            {item.clientNotes && <span>(Client Notes: {item.clientNotes})</span>}
          </>
        );
      case 'address':
        return (
          <>
            <strong>{item.label}:</strong>{' '}
            {item.street || item.city || item.state || item.zip
              ? `${item.street || ''}${item.street && (item.city || item.state || item.zip) ? ', ' : ''}${item.city || ''}${(item.city && (item.state || item.zip)) ? ', ' : ''}${item.state || ''}${item.state && item.zip ? ' ' : ''}${item.zip || ''}`
              : 'Not specified'}{' '}
            {item.clientNotes && <span>(Client Notes: {item.clientNotes})</span>}
          </>
        );
      case 'date':
        return (
          <>
            <strong>{item.label}:</strong> {item.date || 'Not specified'}{' '}
            {item.time && <span><strong>Time:</strong> {item.time}</span>}{' '}
            {item.clientNotes && <span>(Client Notes: {item.clientNotes})</span>}
          </>
        );
      case 'note':
        return (
          <>
            <strong>Note:</strong> {item.content || 'Not specified'}{' '}
            {item.clientNotes && <span>(Client Notes: {item.clientNotes})</span>}
          </>
        );
      default:
        return <span>Unknown item type</span>;
    }
  };

  return (
    <div className="container mt-5">
      <h2 className="mb-4 text-center">
        Form Details: {form.name || `ID: ${form.id}`}
      </h2>
      <div className="card shadow-sm mb-4">
        <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
          <h4 className="mb-0">Form Information</h4>
          <div>
            <button onClick={exportToPDF} className="btn btn-success btn-sm me-2">
              Export to PDF
            </button>
            <button onClick={deleteForm} className="btn btn-danger btn-sm">
              Delete Form
            </button>
          </div>
        </div>
        <div className="card-body">
          <p><strong>Name:</strong> {form.name || 'Not set'}</p>
          <p><strong>Status:</strong> {form.status}</p>
          <p><strong>Last Updated:</strong> {new Date(form.lastUpdated).toLocaleString()}</p>
          <div className="mb-3">
            <label className="form-label"><strong>Due Date:</strong></label>
            <div className="d-flex align-items-center">
              <input
                type="date"
                className="form-control me-2"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                style={{ maxWidth: '200px' }}
              />
              <button onClick={updateDueDate} className="btn btn-primary">
                Set Due Date
              </button>
            </div>
          </div>
          <div className="mb-3">
            <label className="form-label"><strong>Notes:</strong></label>
            <div className="d-flex align-items-start">
              <textarea
                className="form-control me-2"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={5}
                placeholder="Add notes here..."
              />
              <button onClick={updateNotes} className="btn btn-primary">
                Save Notes
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="card shadow-sm">
        <div className="card-header bg-primary text-white">
          <h4 className="mb-0">Form Data</h4>
        </div>
        <div className="card-body">
          {formData.sections && formData.sections.length > 0 ? (
            formData.sections.map((section: Section, sectionIndex: number) => (
              <div key={sectionIndex} className="card mb-3 shadow-sm">
                <div className="card-header bg-secondary text-white">
                  <h5 className="mb-0">{section.label}</h5>
                </div>
                <div className="card-body">
                  {section.clientNote && <p><strong>Client Note:</strong> {section.clientNote}</p>}
                  <ul className="list-group list-group-flush">
                    {section.items.map((item: Item, itemIndex: number) => (
                      <li key={itemIndex} className="list-group-item">
                        {renderItemContent(item)}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))
          ) : (
            <p className="text-muted text-center">No sections specified.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewFormDetails;